import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.css';

// Wraps a single-line, ellipsis-truncated element and shows a themed tooltip
// with the full text — but only when the text is actually truncated. The
// wrapped element only gets `cursor: pointer` in that same case, so hovering
// text that already fits gives no visual hint and no popup.
export default function Tooltip({ children, content, as: Component = 'span', className = '', style, forceOverflow = false, ...rest }) {
  const ref = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(forceOverflow);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);

  useLayoutEffect(() => {
    if (forceOverflow) {
      setIsOverflowing(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const checkOverflow = () => setIsOverflowing(el.scrollWidth > el.clientWidth + 1);
    checkOverflow();

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [forceOverflow]);

  useEffect(() => {
    if (!open) return;
    const handleScrollOrResize = () => setOpen(false);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [open]);

  const handleMouseEnter = (e) => {
    if (forceOverflow) {
      setCoords({ top: e.clientY, left: e.clientX });
      setOpen(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    // Re-measure synchronously on hover rather than trusting only the
    // ResizeObserver-driven state — layout can change (font load, column
    // resize, dynamic content) between mount and the moment the user hovers.
    const overflowing = el.scrollWidth > el.clientWidth + 1;
    if (overflowing !== isOverflowing) setIsOverflowing(overflowing);
    if (!overflowing) return;
    setCoords({ top: e.clientY, left: e.clientX });
    setOpen(true);
  };

  // Tooltip tracks the pointer rather than the element's box, so it stays
  // anchored to wherever the user is actually looking as they hover.
  const handleMouseMove = (e) => {
    if (!isOverflowing) return;
    setCoords({ top: e.clientY, left: e.clientX });
  };

  const handleMouseLeave = () => setOpen(false);

  return (
    <>
      <Component
        ref={ref}
        className={className}
        style={{ ...style, cursor: isOverflowing ? 'pointer' : style?.cursor }}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        {...rest}
      >
        {children}
      </Component>
      {open && isOverflowing && coords && createPortal(
        <div
          className="customer-portal-tooltip"
          style={{ top: coords.top, left: coords.left }}
        >
          {content ?? children}
        </div>,
        document.body
      )}
    </>
  );
}
